// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useMemo } from 'react';
import { formatDateTime } from '@loa/theme';
import { useProviderSubmissions } from '../../hooks/useProviderSubmissions';
import { useAccessTokens } from '../../hooks/useAccessTokens';
import { usePolicyMetadata } from '../../hooks/usePolicyMetadata';
import { LOATableCard } from '../loa/LOATableCard';
import type { LOATableColumn } from '../loa/types';
import type { PolicyLogEntry } from '../../types/policyLog';
import {
  normalizeEditHistory,
  normalizeSubmissions,
  normalizeAccessTokens,
  mergeAndSort,
} from '../../utils/policyLogNormalizer';
import './ProviderSubmissionsTab.css';

type ProviderSubmissionsTabProps = Readonly<{
  policyId: string;
  resolveUser?: (id: string) => string;
}>;

const columns: LOATableColumn<PolicyLogEntry>[] = [
  {
    key: 'date',
    label: 'Date',
    className: 'policy-log__col-date',
    render: (row) => formatDateTime(row.date),
  },
  {
    key: 'user',
    label: 'User',
    className: 'policy-log__col-user',
    render: (row) => row.user,
  },
  {
    key: 'details',
    label: 'Details',
    className: 'policy-log__col-details',
    render: (row) => row.details,
  },
];

export function ProviderSubmissionsTab({ policyId, resolveUser }: ProviderSubmissionsTabProps) {
  const {
    data: submissions,
    loading: submissionsLoading,
    error: submissionsError,
  } = useProviderSubmissions(policyId);

  const {
    data: tokens,
    loading: tokensLoading,
    error: tokensError,
  } = useAccessTokens(policyId);

  const { editHistory } = usePolicyMetadata(policyId);

  const loading = submissionsLoading || tokensLoading;
  const error = submissionsError || tokensError;

  const logEntries = useMemo(() => {
    const editEntries = normalizeEditHistory(editHistory, resolveUser);
    const submissionEntries = normalizeSubmissions(submissions || []);
    const tokenEntries = normalizeAccessTokens(tokens || [], resolveUser);
    return mergeAndSort(editEntries, submissionEntries, tokenEntries);
  }, [editHistory, submissions, tokens, resolveUser]);

  return (
    <LOATableCard<PolicyLogEntry>
      data={logEntries}
      columns={columns}
      keyExtractor={(row) => row.id}
      loading={loading}
      error={error}
      title="Log"
      emptyMessage="No activity recorded for this policy yet."
    />
  );
}
